import { useState } from "react";
import { trpc } from "../lib/trpc.js";
import { RichMarkdown } from "../components/RichContent.js";
import {
  Database, Search, RefreshCw, BookOpen, Brain, Lightbulb, AlertTriangle,
  FileText, Clock, Tag, ChevronDown, ChevronUp, Loader2, Trash2,
  FolderOpen, Layers, Filter, Plus, Zap, CheckCircle2, AlertCircle,
  MessageCircle, Globe, GitBranch, Link, Youtube, Upload, File,
  ExternalLink,
} from "lucide-react";

type MemoryTab = "knowledge" | "ingest" | "learnings" | "sessions";

// ─── Knowledge Base Tab (Embeddings) ─────────────────────────────────────────

function KnowledgeTab() {
  const [searchQuery, setSearchQuery] = useState("");
  const [showAddForm, setShowAddForm] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [newContent, setNewContent] = useState("");

  const { data: kbData, isLoading, refetch } = trpc.knowledge.list.useQuery();
  const { data: stats } = trpc.knowledge.stats.useQuery();

  const searchMutation = trpc.knowledge.search.useMutation();
  const indexMutation = trpc.knowledge.indexEntries.useMutation({
    onSuccess: () => refetch(),
  });
  const addMutation = trpc.knowledge.add.useMutation({
    onSuccess: () => { refetch(); setShowAddForm(false); setNewTitle(""); setNewContent(""); },
  });
  const deleteMutation = trpc.knowledge.delete.useMutation({
    onSuccess: () => refetch(),
  });

  const handleSearch = () => {
    if (searchQuery.length > 2) {
      searchMutation.mutate({ query: searchQuery, topK: 20 });
    }
  };

  const entries = searchMutation.data?.results || kbData?.entries || [];
  const isSearchMode = !!(searchQuery.length > 2 && searchMutation.data);

  const importMutation = trpc.knowledge.bulkImport.useMutation({
    onSuccess: () => refetch(),
  });
  const [isImporting, setIsImporting] = useState(false);

  const handleImportYOS = async () => {
    setIsImporting(true);
    try {
      const res = await fetch("/api/yos-knowledge-export.json");
      if (!res.ok) {
        // Try to fetch from a known location or show instructions
        alert("Para importar conhecimentos do YOS, use o endpoint knowledge.bulkImport via API ou execute o script de seed.");
        return;
      }
      const data = await res.json();
      // Import in batches of 10
      for (let i = 0; i < data.length; i += 10) {
        const batch = data.slice(i, i + 10);
        await importMutation.mutateAsync({ entries: batch });
      }
    } catch (err) {
      console.error("Import error:", err);
    } finally {
      setIsImporting(false);
    }
  };

  const sourceIcons: Record<string, typeof FileText> = {
    chat: MessageCircle,
    task: Brain,
    manual: FileText,
    connector: Globe,
    yos_knowledge: BookOpen,
    yos_playbook: Layers,
    yos_agent: Brain,
    yos_skill: Zap,
    yos_learning: Lightbulb,
    yos_decision: GitBranch,
    yos_core: Database,
    yos_docs: FileText,
    yos_template: FolderOpen,
  };

  const sourceLabels: Record<string, string> = {
    chat: "Conversa",
    task: "Task",
    manual: "Manual",
    connector: "Conector",
    yos_knowledge: "YOS Dom\u00ednio",
    yos_playbook: "YOS Playbook",
    yos_agent: "YOS Agente",
    yos_skill: "YOS Skill",
    yos_learning: "YOS Aprendizado",
    yos_decision: "YOS Decis\u00e3o",
    yos_core: "YOS Core",
    yos_docs: "YOS Docs",
    yos_template: "YOS Template",
  };

  return (
    <div className="space-y-4">
      {/* Stats Bar */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="bg-card border border-border rounded-xl p-3">
          <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Total</p>
          <p className="text-lg font-bold text-foreground">{stats?.totalEntries || 0}</p>
        </div>
        <div className="bg-card border border-border rounded-xl p-3">
          <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Indexadas</p>
          <p className="text-lg font-bold text-primary">{stats?.indexedEntries || 0}</p>
        </div>
        <div className="bg-card border border-border rounded-xl p-3">
          <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Pendentes</p>
          <p className="text-lg font-bold text-yellow-500">{(stats?.totalEntries || 0) - (stats?.indexedEntries || 0)}</p>
        </div>
        <div className="bg-card border border-border rounded-xl p-3">
          <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Embeddings</p>
          <div className="flex items-center gap-1.5 mt-0.5">
            <div className={`w-2 h-2 rounded-full ${stats?.embeddingProviders?.status === "healthy" ? "bg-success" : stats?.embeddingProviders?.status === "degraded" ? "bg-yellow-500" : "bg-destructive"}`} />
            <p className="text-xs text-foreground capitalize">{stats?.embeddingProviders?.status === "healthy" ? (stats.embeddingProviders.activeProvider || "Online") : stats?.embeddingProviders?.status === "degraded" ? "Degradado" : "Offline"}</p>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex-1 min-w-[200px] relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") handleSearch(); }}
            placeholder="Busca semântica via embeddings..."
            className="w-full pl-10 pr-4 py-2.5 bg-background border border-input rounded-xl text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring/50 placeholder:text-muted-foreground/60"
          />
        </div>
        <button
          onClick={handleSearch}
          disabled={searchQuery.length < 3 || searchMutation.isPending}
          className="flex items-center gap-1.5 px-3 py-2.5 bg-primary text-primary-foreground rounded-xl text-sm font-medium hover:opacity-90 disabled:opacity-40 transition-all"
        >
          {searchMutation.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Search className="w-3.5 h-3.5" />}
          Buscar
        </button>
        <button
          onClick={() => indexMutation.mutate()}
          disabled={indexMutation.isPending}
          className="flex items-center gap-1.5 px-3 py-2.5 bg-violet-500/10 text-violet-400 border border-violet-500/20 rounded-xl text-sm font-medium hover:bg-violet-500/20 disabled:opacity-40 transition-all"
        >
          {indexMutation.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Zap className="w-3.5 h-3.5" />}
          Indexar
        </button>
        <button
          onClick={() => setShowAddForm(!showAddForm)}
          className="flex items-center gap-1.5 px-3 py-2.5 bg-secondary border border-border rounded-xl text-sm font-medium text-foreground hover:bg-secondary/80 transition-all"
        >
          <Plus className="w-3.5 h-3.5" /> Adicionar
        </button>
        <button onClick={() => { refetch(); searchMutation.reset(); setSearchQuery(""); }} className="p-2.5 border border-border rounded-xl text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors">
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      {/* Import YOS Knowledge Banner */}
      <div className="bg-gradient-to-r from-primary/5 to-violet-500/5 border border-primary/20 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
            <Brain className="w-5 h-5 text-primary" />
          </div>
          <div className="flex-1">
            <h3 className="text-sm font-semibold text-foreground">Importar Conhecimentos do YOS</h3>
            <p className="text-xs text-muted-foreground mt-0.5">
              O YOS (Brain OS) possui 74+ entradas de conhecimento incluindo dom\u00ednios, playbooks, agentes, skills e aprendizados.
              Importe-os para a Knowledge Base e indexe via embeddings.
            </p>
          </div>
          <button
            onClick={handleImportYOS}
            disabled={isImporting || importMutation.isPending}
            className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-xl text-sm font-medium hover:opacity-90 disabled:opacity-40 transition-all shrink-0"
          >
            {isImporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Database className="w-4 h-4" />}
            {isImporting ? "Importando..." : "Importar YOS"}
          </button>
        </div>
      </div>

      {/* Import result */}
      {importMutation.data && (
        <div className="flex items-center gap-2 p-3 bg-success/5 border border-success/20 rounded-lg text-sm text-success">
          <CheckCircle2 className="w-4 h-4 shrink-0" />
          {importMutation.data.message}
        </div>
      )}

      {/* Index Result */}
      {indexMutation.data && (
        <div className="flex items-center gap-2 p-3 bg-success/5 border border-success/20 rounded-lg text-sm text-success">
          <CheckCircle2 className="w-4 h-4 shrink-0" />
          {indexMutation.data.message}
        </div>
      )}

      {/* Add Form */}
      {showAddForm && (
        <div className="bg-card border border-primary/20 rounded-xl p-5 space-y-3">
          <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
            <Plus className="w-4 h-4 text-primary" /> Adicionar Conhecimento
          </h3>
          <input
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            placeholder="Título"
            className="w-full px-3 py-2 bg-background border border-input rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring/50"
          />
          <textarea
            value={newContent}
            onChange={(e) => setNewContent(e.target.value)}
            placeholder="Conteúdo (markdown suportado)"
            rows={4}
            className="w-full px-3 py-2 bg-background border border-input rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring/50 resize-none"
          />
          <div className="flex items-center gap-2 justify-end">
            <button onClick={() => setShowAddForm(false)} className="px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors">
              Cancelar
            </button>
            <button
              onClick={() => addMutation.mutate({ title: newTitle, content: newContent })}
              disabled={!newTitle.trim() || !newContent.trim() || addMutation.isPending}
              className="flex items-center gap-1.5 px-4 py-1.5 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:opacity-90 disabled:opacity-40"
            >
              {addMutation.isPending ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
              Salvar
            </button>
          </div>
        </div>
      )}

      {/* Search mode indicator */}
      {isSearchMode && (
        <div className="flex items-center gap-2 px-3 py-2 bg-primary/5 border border-primary/10 rounded-lg">
          <Search className="w-3.5 h-3.5 text-primary" />
          <span className="text-xs text-primary">
            Busca semântica: {searchMutation.data?.results?.length || 0} resultados
            {searchMutation.data?.totalIndexed && ` de ${searchMutation.data.totalIndexed} indexados`}
          </span>
          <button onClick={() => { searchMutation.reset(); setSearchQuery(""); }} className="ml-auto text-xs text-muted-foreground hover:text-foreground">
            Limpar
          </button>
        </div>
      )}

      {/* Results */}
      {isLoading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="animate-pulse bg-card border border-border rounded-xl p-4">
              <div className="h-4 bg-muted rounded w-1/3 mb-3" />
              <div className="h-3 bg-muted rounded w-3/4 mb-2" />
              <div className="h-3 bg-muted rounded w-1/2" />
            </div>
          ))}
        </div>
      ) : entries.length > 0 ? (
        <div className="space-y-2">
          {entries.map((item: any) => {
            const SourceIcon = sourceIcons[item.source] || FileText;
            return <KnowledgeCard key={item.id} item={item} SourceIcon={SourceIcon} sourceLabel={sourceLabels[item.source] || item.source} onDelete={() => deleteMutation.mutate({ id: item.id })} isSearchResult={isSearchMode} />;
          })}
        </div>
      ) : (
        <div className="text-center py-12 bg-card border border-border rounded-xl">
          <Database className="w-10 h-10 text-muted-foreground mx-auto mb-3 opacity-50" />
          <h3 className="text-base font-semibold text-foreground mb-1">
            {searchQuery ? "Nenhum resultado encontrado" : "Knowledge Base vazia"}
          </h3>
          <p className="text-xs text-muted-foreground max-w-md mx-auto">
            {searchQuery
              ? "Tente outros termos de busca ou indexe as entradas pendentes."
              : "Conversas no chat e tasks do Brain alimentam automaticamente a Knowledge Base. Você também pode adicionar entradas manualmente."}
          </p>
        </div>
      )}
    </div>
  );
}

function KnowledgeCard({ item, SourceIcon, sourceLabel, onDelete, isSearchResult }: {
  item: any;
  SourceIcon: typeof FileText;
  sourceLabel: string;
  onDelete: () => void;
  isSearchResult?: boolean;
}) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="bg-card border border-border rounded-xl overflow-hidden hover:border-primary/20 transition-colors">
      <div className="p-4 cursor-pointer" onClick={() => setExpanded(!expanded)}>
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1.5 flex-wrap">
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-secondary text-muted-foreground border border-border flex items-center gap-1">
                <SourceIcon className="w-2.5 h-2.5" /> {sourceLabel}
              </span>
              {item.indexed && (
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-success/10 text-success border border-success/20 flex items-center gap-1">
                  <Zap className="w-2.5 h-2.5" /> Indexada
                </span>
              )}
              {!item.indexed && (
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-yellow-500/10 text-yellow-500 border border-yellow-500/20">
                  Pendente
                </span>
              )}
              {isSearchResult && item.score !== undefined && (
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-primary/10 text-primary border border-primary/20">
                  {Math.round(item.score * 100)}% relevante
                </span>
              )}
            </div>
            <p className="text-sm text-foreground line-clamp-2 font-medium">{item.title || "Sem título"}</p>
            <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{(item.content || "").slice(0, 200)}</p>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <button
              onClick={(e) => { e.stopPropagation(); if (confirm("Remover esta entrada?")) onDelete(); }}
              className="p-1 text-muted-foreground/40 hover:text-destructive rounded transition-colors"
            >
              <Trash2 className="w-3 h-3" />
            </button>
            {expanded ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
          </div>
        </div>
      </div>
      {expanded && (
        <div className="border-t border-border px-4 py-3 bg-background/50">
          <div className="text-sm leading-relaxed max-h-80 overflow-y-auto">
            <RichMarkdown content={item.content || ""} />
          </div>
          <div className="flex items-center gap-3 mt-3 pt-2 border-t border-border/50">
            {item.sourceId && (
              <p className="text-[10px] text-muted-foreground/60 flex items-center gap-1">
                <FileText className="w-3 h-3" /> {item.sourceId.slice(0, 8)}...
              </p>
            )}
            {item.indexedAt && (
              <p className="text-[10px] text-muted-foreground/60 flex items-center gap-1">
                <Zap className="w-3 h-3" /> Indexada: {new Date(item.indexedAt).toLocaleString("pt-BR")}
              </p>
            )}
            {item.createdAt && (
              <p className="text-[10px] text-muted-foreground/60 flex items-center gap-1">
                <Clock className="w-3 h-3" /> {new Date(item.createdAt).toLocaleString("pt-BR")}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Ingest Tab (Content Ingestion Module) ──────────────────────────────────

type IngestMode = "url" | "youtube" | "document" | "paste";

function IngestTab() {
  const [mode, setMode] = useState<IngestMode>("url");
  const [url, setUrl] = useState("");
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [lastResult, setLastResult] = useState<{ success: boolean; message: string } | null>(null);

  const { refetch: refetchKB } = trpc.knowledge.list.useQuery();

  const ingestUrlMutation = trpc.knowledge.ingestUrl.useMutation({
    onSuccess: (data) => {
      setLastResult({ success: true, message: data.message });
      setUrl(""); setTitle("");
      refetchKB();
    },
    onError: (err) => setLastResult({ success: false, message: err.message }),
  });

  const ingestYouTubeMutation = trpc.knowledge.ingestYouTube.useMutation({
    onSuccess: (data) => {
      setLastResult({ success: true, message: data.message });
      setUrl(""); setTitle("");
      refetchKB();
    },
    onError: (err) => setLastResult({ success: false, message: err.message }),
  });

  const ingestDocMutation = trpc.knowledge.ingestDocument.useMutation({
    onSuccess: (data) => {
      setLastResult({ success: true, message: data.message });
      setTitle(""); setContent("");
      refetchKB();
    },
    onError: (err) => setLastResult({ success: false, message: err.message }),
  });

  const addMutation = trpc.knowledge.add.useMutation({
    onSuccess: () => {
      setLastResult({ success: true, message: "Conte\u00fado adicionado com sucesso" });
      setTitle(""); setContent("");
      refetchKB();
    },
    onError: (err) => setLastResult({ success: false, message: err.message }),
  });

  const isPending = ingestUrlMutation.isPending || ingestYouTubeMutation.isPending || ingestDocMutation.isPending || addMutation.isPending;

  const handleSubmit = () => {
    setLastResult(null);
    if (mode === "url" && url) {
      ingestUrlMutation.mutate({ url, title: title || undefined });
    } else if (mode === "youtube" && url) {
      ingestYouTubeMutation.mutate({ url, title: title || undefined });
    } else if (mode === "document" && title && content) {
      ingestDocMutation.mutate({ title, content, source: "document" });
    } else if (mode === "paste" && title && content) {
      addMutation.mutate({ title, content, source: "manual" });
    }
  };

  const modes: { id: IngestMode; label: string; icon: typeof Globe; desc: string }[] = [
    { id: "url", label: "URL / Website", icon: Link, desc: "Extrair conte\u00fado de qualquer p\u00e1gina web" },
    { id: "youtube", label: "YouTube", icon: Youtube, desc: "Importar transcri\u00e7\u00e3o de v\u00eddeos" },
    { id: "document", label: "Documento", icon: File, desc: "Colar conte\u00fado de PDF, DOCX, etc." },
    { id: "paste", label: "Texto Livre", icon: FileText, desc: "Adicionar conhecimento manualmente" },
  ];

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="bg-gradient-to-r from-primary/5 to-violet-500/5 border border-primary/20 rounded-xl p-5">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
            <Upload className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h3 className="text-base font-semibold text-foreground">Ingest\u00e3o de Conte\u00fado</h3>
            <p className="text-xs text-muted-foreground mt-0.5">
              Importe conte\u00fado de diversas fontes para a Knowledge Base. Ap\u00f3s a ingest\u00e3o, indexe via embeddings para habilitar busca sem\u00e2ntica.
            </p>
          </div>
        </div>
      </div>

      {/* Mode Selector */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {modes.map((m) => {
          const Icon = m.icon;
          return (
            <button
              key={m.id}
              onClick={() => { setMode(m.id); setLastResult(null); }}
              className={`p-4 rounded-xl border text-left transition-all ${
                mode === m.id
                  ? "bg-primary/5 border-primary/30 shadow-sm"
                  : "bg-card border-border hover:border-primary/20"
              }`}
            >
              <Icon className={`w-5 h-5 mb-2 ${mode === m.id ? "text-primary" : "text-muted-foreground"}`} />
              <p className={`text-sm font-medium ${mode === m.id ? "text-foreground" : "text-muted-foreground"}`}>{m.label}</p>
              <p className="text-[10px] text-muted-foreground mt-0.5">{m.desc}</p>
            </button>
          );
        })}
      </div>

      {/* Form */}
      <div className="bg-card border border-border rounded-xl p-5 space-y-4">
        {(mode === "url" || mode === "youtube") && (
          <>
            <div>
              <label className="block text-sm text-muted-foreground mb-1">
                {mode === "url" ? "URL da p\u00e1gina" : "URL do v\u00eddeo YouTube"}
              </label>
              <div className="relative">
                {mode === "youtube" ? (
                  <Youtube className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-red-400" />
                ) : (
                  <Link className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                )}
                <input
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder={mode === "url" ? "https://exemplo.com/artigo" : "https://youtube.com/watch?v=..."}
                  className="w-full pl-10 pr-4 py-2.5 bg-background border border-input rounded-xl text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring/50 placeholder:text-muted-foreground/60"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm text-muted-foreground mb-1">T\u00edtulo (opcional)</label>
              <input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Ser\u00e1 extra\u00eddo automaticamente se n\u00e3o informado"
                className="w-full px-3 py-2.5 bg-background border border-input rounded-xl text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring/50 placeholder:text-muted-foreground/60"
              />
            </div>
          </>
        )}

        {(mode === "document" || mode === "paste") && (
          <>
            <div>
              <label className="block text-sm text-muted-foreground mb-1">T\u00edtulo</label>
              <input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder={mode === "document" ? "Nome do documento" : "T\u00edtulo do conhecimento"}
                className="w-full px-3 py-2.5 bg-background border border-input rounded-xl text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring/50 placeholder:text-muted-foreground/60"
              />
            </div>
            <div>
              <label className="block text-sm text-muted-foreground mb-1">
                {mode === "document" ? "Conte\u00fado do documento (cole o texto extra\u00eddo)" : "Conte\u00fado"}
              </label>
              <textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder={mode === "document" ? "Cole aqui o texto do PDF, DOCX ou outro documento..." : "Digite ou cole o conhecimento aqui..."}
                rows={8}
                className="w-full px-3 py-2.5 bg-background border border-input rounded-xl text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring/50 placeholder:text-muted-foreground/60 resize-none"
              />
              <p className="text-[10px] text-muted-foreground mt-1">
                {content.length.toLocaleString()} caracteres (m\u00e1x 50.000)
              </p>
            </div>
          </>
        )}

        {/* Result */}
        {lastResult && (
          <div className={`flex items-center gap-2 p-3 rounded-lg text-sm ${
            lastResult.success
              ? "bg-success/5 border border-success/20 text-success"
              : "bg-red-500/5 border border-red-500/20 text-red-400"
          }`}>
            {lastResult.success ? <CheckCircle2 className="w-4 h-4 shrink-0" /> : <AlertCircle className="w-4 h-4 shrink-0" />}
            {lastResult.message}
          </div>
        )}

        {/* Submit */}
        <div className="flex items-center justify-between">
          <p className="text-[10px] text-muted-foreground">
            Ap\u00f3s ingest\u00e3o, v\u00e1 para a tab "Knowledge Base" e clique em "Indexar" para habilitar busca sem\u00e2ntica.
          </p>
          <button
            onClick={handleSubmit}
            disabled={isPending || ((mode === "url" || mode === "youtube") && !url) || ((mode === "document" || mode === "paste") && (!title || !content))}
            className="flex items-center gap-2 px-5 py-2.5 bg-primary text-primary-foreground rounded-xl text-sm font-medium hover:opacity-90 disabled:opacity-40 transition-all"
          >
            {isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            {isPending ? "Processando..." : "Ingerir Conte\u00fado"}
          </button>
        </div>
      </div>

      {/* Tips */}
      <div className="bg-card border border-border rounded-xl p-4">
        <h4 className="text-sm font-medium text-foreground mb-2 flex items-center gap-2">
          <Lightbulb className="w-4 h-4 text-yellow-500" /> Dicas de Ingest\u00e3o
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs text-muted-foreground">
          <div className="flex items-start gap-2">
            <Link className="w-3.5 h-3.5 mt-0.5 text-primary shrink-0" />
            <p><strong className="text-foreground">URLs:</strong> Artigos, documenta\u00e7\u00f5es, blogs. O conte\u00fado HTML \u00e9 extra\u00eddo automaticamente.</p>
          </div>
          <div className="flex items-start gap-2">
            <Youtube className="w-3.5 h-3.5 mt-0.5 text-red-400 shrink-0" />
            <p><strong className="text-foreground">YouTube:</strong> Transcri\u00e7\u00f5es s\u00e3o extra\u00eddas automaticamente quando dispon\u00edveis.</p>
          </div>
          <div className="flex items-start gap-2">
            <File className="w-3.5 h-3.5 mt-0.5 text-blue-400 shrink-0" />
            <p><strong className="text-foreground">Documentos:</strong> Copie o texto de PDFs ou DOCX e cole no campo de conte\u00fado.</p>
          </div>
          <div className="flex items-start gap-2">
            <MessageCircle className="w-3.5 h-3.5 mt-0.5 text-success shrink-0" />
            <p><strong className="text-foreground">Conversas:</strong> Chat e Tasks s\u00e3o capturados automaticamente na Knowledge Base.</p>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Learnings Tab ────────────────────────────────────────────────────────────

function LearningsTab() {
  const { data: learnings, isLoading } = trpc.memory.getLearnings.useQuery();
  const { data: kbData } = trpc.knowledge.list.useQuery({ source: "chat" });

  if (isLoading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3].map((i) => (
          <div key={i} className="animate-pulse bg-card border border-border rounded-xl p-4">
            <div className="h-4 bg-muted rounded w-1/2 mb-2" />
            <div className="h-3 bg-muted rounded w-3/4" />
          </div>
        ))}
      </div>
    );
  }

  // Merge Brain API learnings with KB entries from chat
  const brainLearnings = learnings || [];
  const kbLearnings = (kbData?.entries || []).map((e: any) => ({
    lesson: e.title,
    content: e.content,
    context: `Fonte: ${e.source} | ${e.indexed ? "Indexada" : "Pendente indexação"}`,
    agent: "megan",
    createdAt: e.createdAt,
    source: "knowledge_base",
  }));

  const allItems = [...brainLearnings, ...kbLearnings].sort((a: any, b: any) => (b.createdAt || 0) - (a.createdAt || 0));

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 p-3 bg-primary/5 border border-primary/10 rounded-lg">
        <Brain className="w-4 h-4 text-primary" />
        <span className="text-xs text-primary">
          Aprendizados são gerados automaticamente a partir de conversas no chat e tasks. Indexados via embeddings para busca semântica.
        </span>
      </div>

      {allItems.length > 0 ? (
        <div className="space-y-2">
          {allItems.map((item: any, i: number) => (
            <div key={i} className="bg-card border border-border rounded-xl p-4 hover:border-primary/20 transition-colors">
              <div className="flex items-start gap-3">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
                  item.source === "knowledge_base" ? "bg-primary/10" : "bg-yellow-500/10"
                }`}>
                  {item.source === "knowledge_base" ? (
                    <Database className="w-4 h-4 text-primary" />
                  ) : (
                    <Lightbulb className="w-4 h-4 text-yellow-500" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-foreground font-medium">{item.lesson || item.content || item.text}</p>
                  {item.context && (
                    <p className="text-xs text-muted-foreground mt-1">{item.context}</p>
                  )}
                  <div className="flex items-center gap-3 mt-2 flex-wrap">
                    {item.agent && (
                      <span className="text-[10px] text-muted-foreground flex items-center gap-1">
                        <Brain className="w-3 h-3" /> {item.agent}
                      </span>
                    )}
                    {item.source === "knowledge_base" && (
                      <span className="text-[10px] text-primary flex items-center gap-1">
                        <Zap className="w-3 h-3" /> Embeddings
                      </span>
                    )}
                    {item.createdAt && (
                      <span className="text-[10px] text-muted-foreground flex items-center gap-1">
                        <Clock className="w-3 h-3" /> {new Date(item.createdAt).toLocaleString("pt-BR")}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-12 bg-card border border-border rounded-xl">
          <Lightbulb className="w-10 h-10 text-muted-foreground mx-auto mb-3 opacity-50" />
          <h3 className="text-base font-semibold text-foreground mb-1">Nenhum aprendizado</h3>
          <p className="text-xs text-muted-foreground">Conversas no chat e tasks geram aprendizados automaticamente.</p>
        </div>
      )}
    </div>
  );
}

// ─── Sessions Tab ─────────────────────────────────────────────────────────────

function SessionsTab() {
  const { data: sessions, isLoading } = trpc.memory.getSessions.useQuery();

  if (isLoading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3].map((i) => (
          <div key={i} className="animate-pulse bg-card border border-border rounded-xl p-4">
            <div className="h-4 bg-muted rounded w-1/3 mb-2" />
            <div className="h-3 bg-muted rounded w-1/2" />
          </div>
        ))}
      </div>
    );
  }

  const items = sessions || [];

  return (
    <div className="space-y-2">
      {items.length > 0 ? items.map((item: any, i: number) => (
        <div key={i} className="bg-card border border-border rounded-xl p-4 hover:border-primary/20 transition-colors">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-blue-500/10 flex items-center justify-center shrink-0">
                <Layers className="w-4 h-4 text-blue-500" />
              </div>
              <div>
                <p className="text-sm font-medium text-foreground">{item.id || item.sessionId || `Sessão ${i + 1}`}</p>
                <p className="text-[10px] text-muted-foreground">
                  {item.agent && `${item.agent} · `}
                  {item.createdAt && new Date(item.createdAt).toLocaleString("pt-BR")}
                </p>
              </div>
            </div>
            <span className={`text-[10px] px-2 py-0.5 rounded-full border ${
              item.status === "active" ? "bg-success/10 text-success border-success/20" : "bg-secondary text-muted-foreground border-border"
            }`}>
              {item.status === "active" ? "Ativa" : "Encerrada"}
            </span>
          </div>
        </div>
      )) : (
        <div className="text-center py-12 bg-card border border-border rounded-xl">
          <Layers className="w-10 h-10 text-muted-foreground mx-auto mb-3 opacity-50" />
          <h3 className="text-base font-semibold text-foreground mb-1">Nenhuma sessão</h3>
          <p className="text-xs text-muted-foreground">Sessões de trabalho dos agentes aparecerão aqui.</p>
        </div>
      )}
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export function Memory() {
  const [activeTab, setActiveTab] = useState<MemoryTab>("knowledge");

  const tabs: { id: MemoryTab; label: string; icon: typeof Database }[] = [
    { id: "knowledge", label: "Knowledge Base", icon: BookOpen },
    { id: "ingest", label: "Ingestão", icon: Globe },
    { id: "learnings", label: "Aprendizados", icon: Lightbulb },
    { id: "sessions", label: "Sessões", icon: Layers },
  ];

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-bold text-foreground flex items-center gap-2.5">
          <Database className="w-6 h-6 text-primary" />
          Memória
        </h1>
        <p className="text-muted-foreground text-sm mt-1">
          Knowledge Base com busca semântica via embeddings — alimentada automaticamente por conversas e tasks
        </p>
      </div>

      <div className="flex items-center gap-1 p-1 bg-secondary/50 border border-border rounded-xl w-fit">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              activeTab === tab.id
                ? "bg-card text-foreground shadow-sm border border-border"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            <tab.icon className={`w-4 h-4 ${activeTab === tab.id ? "text-primary" : ""}`} />
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === "knowledge" && <KnowledgeTab />}
      {activeTab === "ingest" && <IngestTab />}
      {activeTab === "learnings" && <LearningsTab />}
      {activeTab === "sessions" && <SessionsTab />}
    </div>
  );
}
